import { Injectable, EventEmitter } from '@angular/core';
import {
  SubscriptionsService,
  ListHelperService,
  ProfileService,
  StoreService,
  CountriesService,
  LocalesService,
  PopUpService
} from 'core/services';
import { Subscription } from 'core/models/subscription';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { DateTime } from 'luxon';

@Injectable()
export class SubscriptionListService {
  userRole: any;
  subscription: Subscription[];
  public selectedSubscription: Subscription;
  actionEmitter: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    public subscriptionsService: SubscriptionsService,
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public countriesService: CountriesService,
    public locales: LocalesService,
    public popupService: PopUpService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  getActionEmitter() {
    return this.actionEmitter;
  }

  async getDatasource(listId, options: any = {}) {
    const dataSource = new TableDatasource({
      sort: '-endDate',
      aggregates: 'valid',
      columns: this.getColumns({ timezone: options.timezone }),
      load: (props) => this.fetch(props, dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { clientId, type, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());

    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      clientId,
      type,
      $filter
    };
    return this.subscriptionsService.fetch(paginationOptions).then((response) => response);
  }

  getColumns({ timezone = 'utc' }) {
    const columns = [
      {
        source: 'rateLimit',
        label: this.translate.instant('subscriptions.usersRateLimit'),
        allowFiltering: false,
        sortable: true
      },
      {
        source: 'created_at',
        label: this.translate.instant('nouns.assignDate'),
        sortable: true,
        allowFiltering: false,
        dataType: DataTypes.datetime,
        valueParser: (value, subscription) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, 'utc', format);
        }
      },

      {
        source: 'startDate',
        label: this.translate.instant('credits.header.initialDate'),
        sortable: true,
        dataType: DataTypes.datetime,
        valueParser: (value, subscription) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, timezone, format) + ' (' + timezone + ')';
        }
      },
      {
        source: 'endDate',
        label: this.translate.instant('credits.header.expirationDate'),
        sortable: true,
        dataType: DataTypes.datetime,
        valueParser: (value, subscription) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return subscription.valid
            ? this.listHelper.parseDate(value, timezone, format) + ' (' + timezone + ')'
            : this.translate.instant('clients.credits.notValid');
        }
      },
      {
        source: 'comments',
        label: this.translate.instant('credits.header.comments'),
        visible: this.profileService.canExecute('credits.comments'),
        sortable: false
      },
      {
        source: 'downloadReport',
        allowFiltering: false,
        sortable: false,
        label: this.translate.instant('actions.download'),
        visible: this.profileService.canExecute('subscriptions.downloadReport'),
        showInColumnChooser: false,
        style: { width: '100px' },
        cellRenderer: (renderer, value, data) => {
          const label = this.translate.instant('actions.download');
          return renderer`
            <gl-context-menu small>
              <gl-button slot="target" id="${data.field1}" icon icon-center="glicon-dots"></gl-button>
              <gl-menu-item text="${label}" @click="${() => this.downloadSubscriptionReport(data)}"></gl-menu-item>
            </gl-context-menu>
            `;
        }
      }
    ];
    return columns;
  }

  downloadSubscriptionReport(subscription: Subscription) {
    this.actionEmitter.emit({ action: 'download', subscription });
  }

  getCustomFilters() {
    return {};
  }
}
